import { zodResolver } from '@hookform/resolvers/zod';
import * as m from '@staiter/i18n';
import { ProjectUpsertInput } from '@staiter/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PageHeader } from '~/components/data';
import { CounterpartyPicker } from '~/components/domain/CounterpartyPicker';
import { SalesOrderPicker } from '~/components/domain/SalesOrderPicker';
import { ProjectMembersPicker } from '~/components/domain/project/ProjectMembersPicker';
import { Button, Combobox, DatePicker, Input, Select, Textarea } from '~/components/primitives';
import { AnchorPreview } from '~/components/tasks/blueprints/AnchorPreview';
import { ApiRequestError } from '~/lib/api/client';
import { createProject } from '~/lib/api/projects';
import { fetchSalesOrder } from '~/lib/api/sales-orders';
import {
  deriveAnchorPreview,
  fetchBlueprint,
  fetchBlueprints,
  materializeBlueprint,
} from '~/lib/tasks/api/blueprints';
// С-G Р.7 — дата по умолчанию — деловой день (`/auth/me`), не локальный день браузера.
import { businessTodayIso } from '~/lib/tasks/business-day';
import { useActiveEntityStore } from '~/store/active-entity';
import styles from './_auth.sales-orders.new.module.css';

// S12 — без .strict(): неизвестные query-параметры (?utm_*/?fbclid) стрипаются, а не роняют app.
const newSearchSchema = z.object({
  sales_order_id: z.string().uuid().optional().catch(undefined),
});

/**
 * G16 — Project create form через useForm + zodResolver.
 * Sprint M+ FE-12: rewrite to use primitives (Input/Select/DatePicker/Textarea)
 * + sticky footer + Cancel + visible missing-fields hint. Reuses SO/new CSS module.
 */
export const Route = createFileRoute('/_auth/projects/new')({
  validateSearch: newSearchSchema,
  component: ProjectNewPage,
});

type ProjectTypeValue = 'supply' | 'manufacturing' | 'rnd' | 'modernization' | 'other';

type FormData = {
  name: string;
  /** access-go-live — компания проекта (мульти-доступ / «Все компании»); '' = активная. */
  entity_id?: string | null;
  customer_id: string;
  sales_order_id?: string | null;
  project_type?: ProjectTypeValue | null;
  expected_start_date?: string | null;
  expected_end_date?: string | null;
  estimated_cost?: { amount: string; currency: 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY' };
  pm_user_id?: string | null;
  notes?: string | null;
  /** project-visibility (F1) — открытый (вся компания) | закрытый (только участники). */
  visibility?: 'open' | 'private';
};

function ProjectNewPage(): JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/projects/new' });

  // access-go-live — выбор компании при мульти-доступе: проект больше не падает молча
  // в активную/дефолтную компанию (инцидент: «InsulTech» создался в ECOTEC).
  const memberships = useActiveEntityStore((s) => s.memberships);
  const activeEntityId = useActiveEntityStore((s) => s.activeEntityId);
  const companyOptions = [
    ...new Map(memberships.map((mb) => [mb.entity_id, mb.entity_name])).entries(),
  ].map(([value, label]) => ({ value, label }));
  const showCompanySelect = companyOptions.length > 1;

  const form = useForm<FormData>({
    resolver: zodResolver(ProjectUpsertInput),
    mode: 'onChange',
    // FE-113: estimated_cost amount default = '' (placeholder shown), не '0' — distinguishable
    // от «не указано».
    defaultValues: {
      name: '',
      entity_id: activeEntityId ?? '',
      customer_id: '',
      sales_order_id: null,
      project_type: null,
      expected_start_date: null,
      expected_end_date: null,
      estimated_cost: { amount: '', currency: 'KZT' },
      pm_user_id: null,
      notes: null,
      visibility: 'open',
    },
  });

  // Seed-опции для пикеров при префилле из заказа (имя/код вместо UUID).
  const [customerSeed, setCustomerSeed] = useState<{ value: string; label: string } | null>(null);
  const [soSeed, setSoSeed] = useState<{ value: string; label: string } | null>(null);

  // project-visibility (F17, §3.8) — участники закрытого проекта прямо на форме.
  // Смена компании сбрасывает выбранных: пул другой компании (обрабатывается в onChange
  // Select компании ниже).
  const [members, setMembers] = useState<{ id: string; name: string }[]>([]);

  // project-templates F2.6 — префилл из шаблона (один поток, значения формы выигрывают).
  const [selectedTemplate, setSelectedTemplate] = useState<{
    id: string;
    name: string;
    entity_id: string;
  } | null>(null);
  const [templatePreview, setTemplatePreview] = useState<{
    tasks: number;
    days: number;
    hasDates: boolean;
    anchorTitle: string | null;
  } | null>(null);
  const blueprintsQuery = useQuery({
    queryKey: ['task-module', 'blueprints'],
    queryFn: fetchBlueprints,
  });
  const activeBlueprints = (blueprintsQuery.data ?? []).filter((b) => b.is_active);

  // G16.7 — Pre-fill from `?sales_order_id=...`
  useEffect(() => {
    if (!search.sales_order_id) return;
    fetchSalesOrder(search.sales_order_id).then((so) => {
      form.setValue('sales_order_id', so.id);
      form.setValue('customer_id', so.counterparty_id);
      form.setValue('estimated_cost', { amount: '', currency: so.currency });
      setSoSeed({ value: so.id, label: so.code });
      if (so.counterparty_name) {
        setCustomerSeed({ value: so.counterparty_id, label: so.counterparty_name });
      }
    });
  }, [search.sales_order_id, form]);

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      createProject({
        name: data.name,
        // access-go-live — явная компания (пустой Select → null → активная на BE).
        entity_id: data.entity_id || null,
        // task-quick-capture B1 — клиент опционален: пустой → null (не '' → fail UUID).
        customer_id: data.customer_id || null,
        sales_order_id: data.sales_order_id || null,
        project_type: data.project_type || null,
        expected_start_date: data.expected_start_date || null,
        expected_end_date: data.expected_end_date || null,
        estimated_cost: data.estimated_cost,
        pm_user_id: data.pm_user_id || null,
        notes: data.notes || null,
        visibility: data.visibility ?? 'open',
        // project-visibility (F17/B24, §3.8) — участники с формы (только для закрытого).
        ...(data.visibility === 'private' && members.length > 0
          ? { members: members.map((u) => u.id) }
          : {}),
      }),
    onSuccess: (p) => {
      // access-go-live — проект создан в ДРУГОЙ компании при single-scope: сначала
      // переключаем активный контекст (иначе detail сразу ловит легитимный 404),
      // а вместо detail идём в список — active-entity-sync через ~100ms пересоздаст
      // кэш и увёл бы с detail-URL в любом случае.
      const store = useActiveEntityStore.getState();
      const chosen = form.getValues('entity_id') || null;
      if (
        store.activeScope === 'entity' &&
        chosen &&
        store.activeEntityId &&
        chosen !== store.activeEntityId
      ) {
        store.setActiveEntity(chosen);
        navigate({ to: '/projects' });
        return;
      }
      navigate({ to: '/projects/$id', params: { id: p.id } });
    },
    // FE-63 (Sprint 9) — render BE-10 422 envelope inline через form.setError
    // на каждое поле, чтобы не показывать generic toast при validation fail.
    onError: (err: unknown) => {
      if (err instanceof ApiRequestError && err.code === 'project_create.validation_failed') {
        const details = err.details as
          | { field_errors?: Array<{ field: string; code: string }> }
          | undefined;
        const fieldErrors = details?.field_errors ?? [];
        for (const fe of fieldErrors) {
          form.setError(fe.field as keyof FormData, {
            type: 'server',
            message: fe.code,
          });
        }
      }
    },
  });

  // F2.6 — материализация вместо createProject, когда выбран шаблон.
  const materializeMut = useMutation({
    mutationFn: (data: FormData) =>
      materializeBlueprint(selectedTemplate?.id ?? '', {
        start_date: data.expected_start_date || businessTodayIso(),
        date_mode: 'start_on',
        name: data.name,
        project_type: data.project_type || null,
        estimated_cost: data.estimated_cost?.amount || null,
        notes: data.notes || null,
        customer_id: data.customer_id || null,
        sales_order_id: data.sales_order_id || null,
        copy_assignees: true,
        // project-visibility (F12) — материализация из формы наследует выбор свитчера (F1).
        visibility: data.visibility ?? 'open',
      }),
    onSuccess: (res) =>
      navigate({ to: '/work-tasks/$projectId', params: { projectId: res.project_id } }),
    onError: (err: unknown) => {
      if (err instanceof ApiRequestError) {
        form.setError('name', { type: 'server', message: err.code });
      }
    },
  });

  // Префилл формы из снапшота (однократно; значения формы всегда выигрывают).
  async function onSelectTemplate(id: string): Promise<void> {
    if (!id) {
      setSelectedTemplate(null);
      setTemplatePreview(null);
      return;
    }
    const detail = await fetchBlueprint(id);
    const bpEntity = activeBlueprints.find((b) => b.id === id)?.entity_id ?? '';
    setSelectedTemplate({ id, name: detail.name, entity_id: bpEntity });
    const proj = detail.snapshot?.project;
    // F4.4 — имя из базового имени проекта-источника, не из имени шаблона.
    // F5 — scratch-шаблон источника не имеет (`source: null`): имя шаблона.
    form.setValue('name', detail.snapshot?.source?.project_name ?? detail.name);
    if (proj?.project_type) form.setValue('project_type', proj.project_type as ProjectTypeValue);
    if (proj?.estimated_cost_amount) {
      const cur = proj.currency ?? 'KZT';
      const currency = (['KZT', 'USD', 'EUR', 'RUB', 'CNY'].includes(cur) ? cur : 'KZT') as
        | 'KZT'
        | 'USD'
        | 'EUR'
        | 'RUB'
        | 'CNY';
      form.setValue('estimated_cost', { amount: proj.estimated_cost_amount, currency });
    }
    if (proj?.notes) form.setValue('notes', proj.notes);
    form.setValue('expected_start_date', businessTodayIso());
    // F4.2 — материализация идёт в компанию шаблона (I6): связываем Select компании.
    if (bpEntity) form.setValue('entity_id', bpEntity);
    const preview = deriveAnchorPreview(detail.snapshot ?? null);
    const anchorTitle = preview.anchor_task_key
      ? (detail.snapshot?.tasks.find((t) => t.key === preview.anchor_task_key)?.title ?? null)
      : null;
    setTemplatePreview({
      tasks: detail.snapshot?.tasks.length ?? 0,
      days: preview.max_offset_days,
      hasDates: preview.has_dates,
      anchorTitle,
    });
  }

  const { register, handleSubmit, formState, watch, setValue } = form;
  // task-quick-capture F1 — PM/финанс-поля под аккордеоном; раскрыт сразу, если пришли из
  // заказа клиента (там интент финансовый), иначе свёрнут (контейнер задач = только имя).
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(Boolean(search.sales_order_id));
  const errors = formState.errors;
  const watched = watch();

  // FE-114: project_type enum (SSOT — labels из i18n; не freeform text).
  const projectTypeOptions = [
    { value: 'supply', label: m.project_type_supply() },
    { value: 'manufacturing', label: m.project_type_manufacturing() },
    { value: 'rnd', label: m.project_type_rnd() },
    { value: 'modernization', label: m.project_type_modernization() },
    { value: 'other', label: m.project_type_other() },
  ];

  return (
    <div className={styles.page}>
      <PageHeader title={m.project_title_new()} />
      <form
        onSubmit={handleSubmit((data) =>
          selectedTemplate ? materializeMut.mutate(data) : mutation.mutate(data),
        )}
        noValidate
        className={styles.form}
      >
        {/* project-templates F2.6 — «Создать из шаблона»: префилл формы; submit при
            выбранном шаблоне идёт в materialize (значения формы выигрывают). */}
        {activeBlueprints.length > 0 && (
          <div>
            <Combobox
              label={m.tmpl_prefill_title()}
              value={selectedTemplate?.id ?? ''}
              onChange={(v) => void onSelectTemplate(v)}
              options={activeBlueprints.map((b) => ({ value: b.id, label: b.name }))}
              placeholder={m.tmpl_prefill_select()}
              clearable
            />
            {selectedTemplate && templatePreview && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                  {m.tmpl_tasks_count({ count: templatePreview.tasks })},{' '}
                  {m.tmpl_prefill_plan({ days: templatePreview.days })}
                </span>
                <button
                  type="button"
                  onClick={() => void onSelectTemplate('')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  <X size={12} /> {m.tmpl_prefill_clear()}
                </button>
              </div>
            )}
            {/* F4.3 — превью якоря (единый компонент): пересчёт при смене даты старта. */}
            {selectedTemplate && templatePreview && (
              <div style={{ marginTop: 6 }}>
                <AnchorPreview
                  hasDates={templatePreview.hasDates}
                  maxOffsetDays={templatePreview.days}
                  anchorTitle={templatePreview.anchorTitle}
                  date={watched.expected_start_date ?? null}
                  dateMode="start_on"
                />
              </div>
            )}
          </div>
        )}
        <Input
          label={m.project_form_name()}
          required
          {...register('name')}
          {...(errors.name?.message ? { error: errors.name.message } : {})}
        />
        {/* project-visibility (F1) — открытый / закрытый проект. */}
        <Select
          label={m.project_visibility_label()}
          value={watched.visibility ?? 'open'}
          options={[
            { value: 'open', label: m.project_visibility_open() },
            { value: 'private', label: m.project_visibility_private() },
          ]}
          hint={
            (watched.visibility ?? 'open') === 'private'
              ? `${m.project_visibility_private_hint()}. ${m.project_visibility_owner_note()}`
              : m.project_visibility_open_hint()
          }
          onChange={(e) =>
            setValue('visibility', e.target.value === 'private' ? 'private' : 'open', {
              shouldValidate: true,
            })
          }
        />
        {/* project-visibility (F17, §3.8) — участники закрытого проекта прямо на форме:
            серверный поиск по пользователям ВЫБРАННОЙ компании. Для открытого — скрыто. */}
        {(watched.visibility ?? 'open') === 'private' && (
          <ProjectMembersPicker
            entityId={watched.entity_id || null}
            selected={members}
            onChange={setMembers}
          />
        )}
        {/* access-go-live — компания проекта: видна только при мульти-доступе,
            дефолт — активная компания. Без неё проект молча падал в активную/дефолтную. */}
        {showCompanySelect && (
          <Select
            label={m.project_col_entity()}
            value={watched.entity_id ?? ''}
            options={companyOptions}
            placeholder={m.project_form_select_company()}
            {...(selectedTemplate ? { disabled: true, hint: m.tmpl_company_locked_hint() } : {})}
            onChange={(e) => {
              setValue('entity_id', e.target.value || null, { shouldValidate: true });
              // project-visibility (F17) — пул участников принадлежит компании: смена
              // компании сбрасывает выбранных (id чужой компании не должны уехать в payload).
              setMembers([]);
            }}
          />
        )}
        {/* task-quick-capture F1 — аккордеон «Дополнительно · производство и финансы»: PM/финанс-поля
            свёрнуты по умолчанию (контейнер задач требует только имя). */}
        <button
          type="button"
          className={styles.advancedTrigger}
          aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen((o) => !o)}
        >
          <ChevronRight
            size={16}
            className={advancedOpen ? styles.advancedChevronOpen : styles.advancedChevron}
            aria-hidden="true"
          />
          <span className={styles.advancedLabel}>{m.project_form_advanced_section()}</span>
          <span className={styles.advancedHint}>· {m.project_form_advanced_optional()}</span>
        </button>
        {advancedOpen && (
          <>
            <div className={styles.fields}>
              {/* Клиент-пикер: searchable Combobox с server-side q-поиском по всем контрагентам
                  (kind НЕ ограничиваем — классификация customer/supplier эвристическая, реальный
                  клиент может быть помечен иначе; иначе короткий список без поиска, инцидент 2026-06-29). */}
              <CounterpartyPicker
                label={m.project_col_customer()}
                value={watched.customer_id ?? ''}
                placeholder={m.project_form_select_customer()}
                onChange={(v) => setValue('customer_id', v, { shouldValidate: true })}
                allowCreate
                createKind="customer"
                {...(customerSeed ? { initialOption: customerSeed } : {})}
              />
              <SalesOrderPicker
                label={m.project_form_so()}
                value={watched.sales_order_id ?? ''}
                placeholder={m.project_form_select_so()}
                onChange={(v) => setValue('sales_order_id', v || null, { shouldValidate: true })}
                {...(soSeed ? { initialOption: soSeed } : {})}
              />
              {/* FE-9 (QA 2026-05-31): «Сроки» (мн.ч.) vs «Срок» (ед.ч.) — неинтуитивно;
              ясные labels «Плановый старт» / «Плановый финиш». */}
              <DatePicker
                label={m.project_form_planned_start()}
                value={watched.expected_start_date ?? null}
                onChange={(next) => setValue('expected_start_date', next, { shouldValidate: true })}
              />
              <DatePicker
                label={m.project_form_planned_end()}
                value={watched.expected_end_date ?? null}
                onChange={(next) => setValue('expected_end_date', next, { shouldValidate: true })}
              />
              {/* FE-114: enum-select вместо freeform input. */}
              <Select
                label={m.project_form_project_type()}
                value={watched.project_type ?? ''}
                options={projectTypeOptions}
                placeholder={m.project_form_select_type()}
                onChange={(e) =>
                  setValue('project_type', (e.target.value as ProjectTypeValue) || null, {
                    shouldValidate: true,
                  })
                }
              />
              {/* FE-113: empty default + placeholder 0,00 → distinguishable от explicit zero. */}
              <Input
                label={m.project_form_estimated_cost()}
                type="number"
                step="0.01"
                placeholder="0,00"
                {...register('estimated_cost.amount')}
              />
            </div>
            <Textarea label={m.project_form_notes()} rows={3} {...register('notes')} />
            <p className={styles.advancedNote}>{m.project_form_advanced_note()}</p>
          </>
        )}
        <div className={styles.footer}>
          {/* FE-112: removed summary missing-fields hint — errors теперь inline per field
              (aria-invalid через Input/Select `error` prop). */}
          <div className={styles.actions}>
            {/* F4.9 — «Отмена» ghost (как в диалогах); submit не выглядит disabled
                при валидной форме: на пути шаблона гейт только на имени. */}
            <Button type="button" variant="ghost" onClick={() => navigate({ to: '/projects' })}>
              {m.common_button_cancel()}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending || materializeMut.isPending}
              disabled={
                mutation.isPending ||
                materializeMut.isPending ||
                (selectedTemplate ? !watched.name?.trim() : !formState.isValid)
              }
            >
              {m.project_cta_create()}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
